import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import { Item } from '../../../../../contexts/site/site-context';
import MenuItem from "../items/MenuItem";
import CheckoutButton from '../basket/CheckoutButton';
import { Preferences } from "../../MenuPage";

type UpsellModalProps = {
    upsells: Item[];
    show: boolean;
    onHide: () => void;
    onReturnToOrder: (upsellShowTimeslotHelper: boolean, upsellTimeslotError: string | null, upsellUnavailableScheduledItems: string[]) => void;
    showItemModal: (item: Item, edit: boolean) => void;
    preferences: Preferences
};

const UpsellModal = ({ upsells, show, onHide, onReturnToOrder, showItemModal, preferences }: UpsellModalProps) => {
    const basketContext = useBasketContext();
    const navigate = useNavigate();

    const [navHorizontalScroll, setNavHorizontalScroll] = useState(0);
    const [navHorizontalScrollMax, setNavHorizontalScrollMax] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const bodyContent = useRef<HTMLDivElement>(null);
    const checkoutRef = useRef<HTMLButtonElement>(null);

    const [shadowTop, setShadowTop] = useState(false);
    const [shadowBottom, setShadowBottom] = useState(false);

    const [upsellItems, setUpsellItems] = useState<Item[]>([]);

    const [showTimeslotHelper, setShowTimeslotHelper] = useState(false);
    const [unavailableScheduledItems, setUnavailableScheduledItems] = useState<string[]>([]);
    const [timeslotError, setTimeslotError] = useState<string|null>(null);

    useEffect(() => {
        function updateScrollPosition() {
            if (bodyContent && bodyContent.current ) {
                if(bodyContent?.current?.scrollTop > 78) {
                    setShadowTop(true)
                } else {
                    setShadowTop(false)
                }
                if (bodyContent?.current?.scrollHeight - bodyContent?.current?.scrollTop === bodyContent?.current?.clientHeight) {
                    setShadowBottom(false)
                } else {
                    setShadowBottom(true)
                }
            }
        }

        updateScrollPosition();

        if (bodyContent && bodyContent.current) {
            bodyContent.current.addEventListener("scroll", updateScrollPosition, false);

            document.getElementById('upsell-modal')?.classList.add('anim-enter');

            return () => {
                setShadowTop(false);
                setShadowBottom(false);
                bodyContent?.current?.removeEventListener("scroll", updateScrollPosition, false);
                document.getElementById('upsell-modal')?.classList.remove('anim-enter');
            };
        }
    }, [bodyContent.current, show]);

    useEffect(() => {
      setUpsellItems(upsells);
    }, [upsells]);

    useEffect(() => {
      setUpsellItems(upsellItems.filter((item) => {
          let count = 0;
          basketContext.basketItems
              .filter((i) => item.uid === i.uid)
              .forEach((i) => count += (i.quantity ?? 0));
          return count === 0;
      }) ?? []);
    }, [basketContext.basketItems]);

    useEffect(() => {
    	if (show && upsellItems.length === 0) {
        	checkoutRef.current?.click();
    	}
    }, [upsellItems]);

    useEffect(() => {
      if (showTimeslotHelper || timeslotError !== null || unavailableScheduledItems.length > 0) {
        // Something's wrong with the basket - return to the order summary so the user can sort it out
        onReturnToOrder(showTimeslotHelper, timeslotError, unavailableScheduledItems);
      }
    }, [showTimeslotHelper, timeslotError, unavailableScheduledItems]);

    const renderMenuItems = () => {
        return upsells.map((item, index) =>
            <MenuItem
                key={`mcmi-${item.uid}`}
                item={item}
                showItemModal={showItemModal}
                preferences={preferences}
                variant="portrait"
                fromUpsell={true}
            />
        );
    };

    return (
        <StyledModal
            show={show}
            onHide={onHide}
            size="lg"
            backdrop={true}
            backdropClassName="modal-upsell-backdrop"
            className="upsell-modal-dialog"
            centered
            id={'upsell-modal'}
            animation={false}
        >
            <Modal.Header className="modal-show-header-shadow">
                <h2 className='modal__title'>{t`Forgot something?`}</h2>
                <div className="modal-close__wrapper">
                    <button role="button" className={'modal-close__btn'} onClick={() => onHide()}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body ref={bodyContent}>
                <MenuItems>
                    { renderMenuItems() }
                </MenuItems>
            </Modal.Body>
            <Modal.Footer className="modal-show-footer-shadow">
                <StyledForm>
                    <Form.Group controlId="checkoutForm" className="mb-0">
                      <CheckoutButton
                          showTimeslotHelper={setShowTimeslotHelper}
                          setTimeslotError={setTimeslotError}
                          setUnavailableScheduledItems={setUnavailableScheduledItems}
                          currentlyOnUpsell={true}
                          displayUpsell={false}
                          onContinueToUpsell={() => {}}
                          ref={checkoutRef}
                      />
                    </Form.Group>
                </StyledForm>
            </Modal.Footer>
        </StyledModal>
    );
};

const MenuItems = styled.div`
    padding: 16px;
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 16px;

    @media screen and (min-width: 768px) {
    	grid-template-columns: repeat(3, minmax(160px, 1fr));
    }
`;

const StyledForm = styled(Form)`
    .form-group {
        margin-bottom: 16px;
    }

    width: 100%;
`;

const StyledModal = styled(Modal)`
    .modal-block {
        padding-bottom: 16px;
    }

    .close {
        left: 16px;
        right: auto;
        opacity: 1 !important;
    }

    .modal-content {
        @media screen and (max-width: 767px) {
            height: 100% !important;
        }
    }

    .modal-header {
        display: flex;
        align-items: center;
        height: 56px;
        background-color: transparent;
        z-index: 1;
        width: 100%;
        padding: 16px;

        > div { width: 100% }

        .modal__title {
            margin-bottom: 0;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }
    }

    .modal-body {
        padding: 0;
        padding-bottom: 10px;
        overflow-y: auto;
        font-size: 14px;

        .channel {
            margin-bottom: 16px;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }

        .channel-reduced {
            margin-bottom: 8px !important;
            padding: 16px 16px 0 16px;
        }

        table {
            table-layout: fixed;
            margin: 1.5rem 0;
            tr td {
                line-height: 30px;
                padding: 0;
                border: 0;
                font-weight: 600;
                &:last-child {
                    font-weight: 400;
                    color: var(--text-black);
                }
            }
        }
    }

    .modal-footer {
        padding: 16px !important;
        transition: box-shadow 0.15s ease-in-out;

        .total {
            padding-bottom: 8px;
            display: flex;
            justify-content: space-between;
            font-size: 16px;
            line-height: 24px;
        }
    }
`;

export default UpsellModal;
